import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Cpu, CloudCog, TrendingUp, Braces, Sprout, Radio } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";

const LAUNCH = new Date("2026-12-30T09:00:00+05:30").getTime();

const VERTICALS = [
  {
    icon: Braces,
    title: "On-Demand Application Building",
    copy: "Not off-the-shelf products — applications built to your requirement. If we can cater to it, we come to terms and deliver at a negotiable cost, never at compromised quality.",
  },
  {
    icon: CloudCog,
    title: "Enterprise Cloud Solutions",
    copy: "Architecture, migration and managed-cloud engineering for businesses that outgrew spreadsheets and duct tape.",
  },
  {
    icon: TrendingUp,
    title: "Regional Supply-Chain Analytics",
    copy: "Farm-to-fork visibility across Rajasthan's food economy — demand forecasting, seasonal sourcing and waste reduction.",
  },
];

const ROADMAP = [
  { date: "Dec 2025", title: "The company forms", copy: "TOSIASI Creatives incorporates with food and software under one roof." },
  { date: "2025 — 26", title: "The kitchen gets a Lab Extension", copy: "MOZEKAQL is welcomed into Tosiasi family drama. Nok Chhonk is no longer the lone child." },
  { date: "Dec 2026", title: "Mozekaql launches", copy: "The IT solution architecture and agro-research vertical goes to market." },
];

function useCountdown() {
  const [left, setLeft] = useState(LAUNCH - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(LAUNCH - Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const clamp = Math.max(0, left);
  return {
    days: Math.floor(clamp / 86400000),
    hours: Math.floor((clamp % 86400000) / 3600000),
    minutes: Math.floor((clamp % 3600000) / 60000),
    seconds: Math.floor((clamp % 60000) / 1000),
  };
}

function Countdown() {
  const { days, hours, minutes, seconds } = useCountdown();
  const cells = [
    { v: days, l: "Days" },
    { v: hours, l: "Hours" },
    { v: minutes, l: "Minutes" },
    { v: seconds, l: "Seconds" },
  ];
  return (
    <div className="grid grid-cols-4 gap-3 md:gap-5" data-testid="mozekaql-countdown">
      {cells.map((c) => (
        <div key={c.l} className="glass rounded-2xl px-2 py-5 text-center md:px-6">
          <p className="font-display text-3xl font-semibold tabular-nums text-cyan-300 text-glow-cyan md:text-5xl">
            {String(c.v).padStart(2, "0")}
          </p>
          <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 md:text-[10px]">{c.l}</p>
        </div>
      ))}
    </div>
  );
}

export default function Mozekaql() {
  useEffect(() => {
    document.title = "Mozekaql — IT Solutions & Agro-Research | TOSIASI Creatives";
  }, []);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-40 pb-20 md:pt-48">
        <div className="glow-cyan pointer-events-none absolute -left-40 top-0 h-[560px] w-[560px]" />
        <div className="glow-cyan pointer-events-none absolute -right-40 top-64 h-[520px] w-[520px] opacity-60" />
        <div className="bg-gridlines pointer-events-none absolute inset-0 opacity-70" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="eyebrow"
          >
            IT solution architecture × agro-research
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: EASE }}
            className="mt-8"
          >
            <img
              src="/mozekaql-logo.png"
              alt="Mozekaql — Creative Intellect"
              data-testid="mozekaql-logo"
              className="w-52 rounded-3xl border border-white/10 shadow-[0_24px_90px_rgba(6,182,212,0.3)] md:w-64"
            />
          </motion.div>
          <h1 className="sr-only">Mozekaql — IT Solutions &amp; Agro-Research by TOSIASI Creatives</h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-8 flex max-w-xl flex-wrap items-center gap-3 text-base leading-relaxed text-slate-400 md:text-lg"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">
              <Radio size={12} /> Launching December 30, 2026
            </span>
            <span>On-demand applications, enterprise cloud and supply-chain intelligence — born in a working kitchen.</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-12 max-w-2xl"
          >
            <Countdown />
          </motion.div>
        </div>
      </section>

      {/* VERTICALS */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">What we&apos;re building</p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">
              Three practices, <span className="font-light text-slate-400">one architecture.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {VERTICALS.map((v, i) => (
              <Reveal key={v.title} delay={0.08 * i}>
                <div className="glass card-hover group relative h-full overflow-hidden rounded-3xl p-8" data-testid={`mozekaql-vertical-${i}`}>
                  <div className="glow-cyan pointer-events-none absolute -right-20 -top-20 h-56 w-56 opacity-0 transition-opacity duration-500 group-hover:opacity-80" />
                  <v.icon size={22} className="text-cyan-400" />
                  <h3 className="mt-6 font-display text-xl font-semibold text-white">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{v.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/71daf857-cd31-4408-9b15-b6c959ba23c3/images/9a214bd15229b522aa4be4596d7e6b15b65a8f7edd1a1e59594985d3bec78512.jpeg"
                alt="A kitchen lifting the cloche to find glowing code"
                className="h-72 w-full rounded-3xl border border-white/10 object-cover md:h-96"
              />
              <div className="glass-strong absolute -bottom-5 left-6 flex items-center gap-3 rounded-2xl px-5 py-3.5">
                <Cpu size={18} className="text-cyan-400" />
                <p className="text-xs text-slate-300">A kitchen that finds — and funds — code</p>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal>
              <p className="eyebrow">Why a kitchen funds software</p>
              <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl">
                We feed them. Food <span className="text-cyan-300 text-glow-cyan">FUNDS</span> Code. <span className="font-light text-slate-400">And the future Begins...</span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                Nok Chhonk isn&apos;t a customer story — it&apos;s our production kitchen and our funding line.
                Every thali sold puts real revenue behind Mozekaql&apos;s builders, so the people who build for you
                are backed by a working business, not a pitch deck.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-400">
                That&apos;s the deal behind every on-demand build: a real kitchen pays the way, your requirement
                shapes the scope, the cost stays negotiable — the quality never does.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">Roadmap</p>
            <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">
              The path to <span className="font-light text-cyan-300">December 2026.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {ROADMAP.map((r, i) => (
              <Reveal key={r.title} delay={0.08 * i}>
                <div className="glass card-hover h-full rounded-3xl p-8" data-testid={`mozekaql-roadmap-${i}`}>
                  <div className="flex items-center gap-3">
                    <Sprout size={16} className="text-cyan-400" />
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">{r.date}</p>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-semibold text-white">{r.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{r.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/contact?inquiry=mozekaql-it" data-testid="mozekaql-partner-cta" className="btn btn-cyan">
                Partner early <ArrowRight size={16} />
              </Link>
              <p className="text-xs text-slate-500">Early design-partner conversations open now.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
